import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge, { STATUS_META } from "@/components/StatusBadge";
import { Search, Plus, Filter, Download, Trash2, Eye, X, MessageCircle, FileArchive, Copy, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { canSeeAmount } from "@/lib/roles";
import { exportCSV } from "@/lib/exports";
import usePageTitle from "@/hooks/use-page-title";

export default function Orders() {
  usePageTitle("Orders");
  const { user } = useAuth();
  const showAmount = canSeeAmount(user);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const status = params.get("status") || "";
  const source = params.get("source") || "";
  const dateFrom = params.get("date_from") || "";
  const dateTo = params.get("date_to") || "";
  const amountMin = params.get("amount_min") || "";
  const amountMax = params.get("amount_max") || "";
  const balanceMin = params.get("balance_min") || "";
  const balanceMax = params.get("balance_max") || "";
  const advancedActive = !!(dateFrom || dateTo || amountMin || amountMax || balanceMin || balanceMax);
  const [showAdvanced, setShowAdvanced] = useState(advancedActive);

  const load = () => {
    const qp = {};
    if (q) qp.q = q;
    if (status) qp.status = status;
    if (source) qp.source = source;
    if (dateFrom) qp.date_from = dateFrom;
    if (dateTo) qp.date_to = dateTo;
    if (amountMin) qp.amount_min = amountMin;
    if (amountMax) qp.amount_max = amountMax;
    if (balanceMin) qp.balance_min = balanceMin;
    if (balanceMax) qp.balance_max = balanceMax;
    return api.get("/orders", { params: qp }).then((r) => setRows(r.data));
  };

  useEffect(() => {
    load();
     
  }, [q, status, source, dateFrom, dateTo, amountMin, amountMax, balanceMin, balanceMax]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next);
  };

  const clearAdvanced = () => {
    const next = new URLSearchParams(params);
    ["date_from", "date_to", "amount_min", "amount_max", "balance_min", "balance_max"].forEach((k) => next.delete(k));
    setParams(next);
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Permanently delete order ${o.id}?\n\nAll payments and history will be removed. This cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const [previewUrl, setPreviewUrl] = useState("");
  const [previewOrderId, setPreviewOrderId] = useState("");

  const openPreview = async (o) => {
    try {
      const res = await api.get(`/orders/${o.id}/invoice.pdf?inline=true`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewOrderId(o.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open invoice preview");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setPreviewOrderId("");
  };

  // --- WhatsApp share modal ---
  const [waShare, setWaShare] = useState(null); // { wa_url, message, public_invoice_url, phone, order_id }
  const [waLoading, setWaLoading] = useState("");
  const openWhatsAppShare = async (o) => {
    setWaLoading(o.id);
    try {
      const { data } = await api.get(`/orders/${o.id}/whatsapp-share`);
      setWaShare({ ...data, order_id: o.id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build WhatsApp link");
    } finally {
      setWaLoading("");
    }
  };
  const closeWhatsApp = () => setWaShare(null);

  // --- Bulk Export modal ---
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = new Date().toISOString().slice(0, 8) + "01";
  const [bulk, setBulk] = useState({ open: false, from: firstOfMonth, to: today, fmt: "both", paid_only: false, source: "" });
  const [bulking, setBulking] = useState(false);
  const runBulkExport = async () => {
    setBulking(true);
    try {
      const res = await api.get(`/admin/invoices/bulk-export`, {
        params: { date_from: bulk.from, date_to: bulk.to, fmt: bulk.fmt, paid_only: bulk.paid_only, source: bulk.source || "" },
        responseType: "blob",
      });
      const ext = bulk.fmt === "csv" ? "csv" : "zip";
      const filename = bulk.fmt === "csv"
        ? `GSTR1-Summary-${bulk.from}_to_${bulk.to}.csv`
        : `Invoices-${bulk.from}_to_${bulk.to}.zip`;
      const blob = new Blob([res.data], { type: ext === "csv" ? "text/csv" : "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      const count = res.headers?.["x-invoice-count"];
      toast.success(count ? `Downloaded ${count} invoice${count === "1" ? "" : "s"} for ${bulk.from} → ${bulk.to}` : "Export downloaded");
      setBulk({ ...bulk, open: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk export failed");
    } finally {
      setBulking(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="orders-root">
      <div className="flex items-end justify-between border-b border-[hsl(var(--border))] pb-6">
        <div>
          <div className="overline mb-2">Production</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
            Orders
          </h1>
        </div>
        <Link to="/orders/new" className="btn-swiss btn-accent" data-testid="new-order-btn">
          <Plus size={14} /> New Order
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 flex-1 min-w-[280px] max-w-md">
          <Search size={16} className="text-muted-foreground" />
          <input
            defaultValue={q}
            onChange={(e) => setParam("q", e.target.value)}
            placeholder="Search by order ID, customer, product…"
            className="flex-1 bg-transparent px-3 py-2 text-sm outline-none"
            data-testid="order-search-input"
          />
        </div>
        <div className="flex items-center gap-2 border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-1.5">
          <Filter size={14} className="text-muted-foreground" />
          <select
            value={status}
            onChange={(e) => setParam("status", e.target.value)}
            className="bg-transparent text-sm outline-none"
            data-testid="order-status-filter"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <option key={k} value={k}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Source chips — All / Web / Manual */}
        <div className="inline-flex bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-full p-0.5" data-testid="source-chips">
          {[
            { v: "", l: "All", tid: "src-all" },
            { v: "web", l: "🌐 Web only", tid: "src-web" },
            { v: "manual", l: "🏪 Manual only", tid: "src-manual" },
          ].map((o) => {
            const sel = source === o.v;
            return (
              <button
                key={o.v || "all"}
                type="button"
                onClick={() => setParam("source", o.v)}
                className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${sel ? "bg-[#FF6B00] text-white" : "text-zinc-600 hover:text-[#FF6B00]"}`}
                data-testid={o.tid}
              >
                {o.l}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => exportCSV("orders", rows.map((o) => ({
            OrderID: o.id, Customer: o.customer_name, Company: o.customer_company,
            Product: o.product_name, Category: o.product_category, Qty: o.quantity,
            ...(showAmount ? { Amount: o.estimated_cost, Balance: o.balance } : {}),
            Status: o.status, CreatedAt: o.created_at,
          })))}
          className="btn-swiss btn-outline"
          data-testid="orders-export-csv"
        >
          <Download size={14} /> Export CSV
        </button>
        {isAdmin && (
          <button
            onClick={() => setBulk({ ...bulk, open: true })}
            className="btn-swiss btn-outline"
            data-testid="orders-bulk-invoices-btn"
            title="Download all invoices for a date range (GSTR-1 filing)"
          >
            <FileArchive size={14} /> Bulk Invoices
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className={`btn-swiss btn-outline ${advancedActive ? "border-[#FF6B00] text-[#FF6B00]" : ""}`}
          data-testid="orders-advanced-toggle"
          title="Filter by date, amount or balance range"
        >
          <SlidersHorizontal size={14} /> {showAdvanced ? "Hide filters" : "More filters"}
          {advancedActive && <span className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#FF6B00] text-white text-[9px] font-bold">•</span>}
        </button>
      </div>

      {showAdvanced && (
        <div className="card-swiss p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 items-end" data-testid="orders-advanced-panel">
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">From date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setParam("date_from", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">To date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setParam("date_to", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
              data-testid="filter-date-to"
            />
          </div>
          {showAmount && (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Amount min (₹)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={amountMin}
                  onChange={(e) => setParam("amount_min", e.target.value)}
                  className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
                  data-testid="filter-amount-min"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Amount max (₹)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Any"
                  value={amountMax}
                  onChange={(e) => setParam("amount_max", e.target.value)}
                  className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
                  data-testid="filter-amount-max"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Balance min (₹)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={balanceMin}
                  onChange={(e) => setParam("balance_min", e.target.value)}
                  className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
                  data-testid="filter-balance-min"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Balance max (₹)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Any"
                  value={balanceMax}
                  onChange={(e) => setParam("balance_max", e.target.value)}
                  className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5 text-xs outline-none focus:border-[#FF6B00]"
                  data-testid="filter-balance-max"
                />
              </div>
            </>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearAdvanced}
              disabled={!advancedActive}
              className="btn-swiss btn-outline text-xs w-full disabled:opacity-40"
              data-testid="filter-clear-btn"
            >
              <X size={12} /> Clear
            </button>
          </div>
        </div>
      )}

      <div className="card-swiss overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-4 py-3 text-left overline">Order ID</th>
              <th className="px-4 py-3 text-left overline">Customer</th>
              <th className="px-4 py-3 text-left overline">Product</th>
              <th className="px-4 py-3 text-right overline">Qty</th>
              {showAmount && <th className="px-4 py-3 text-right overline">Amount</th>}
              {showAmount && <th className="px-4 py-3 text-right overline">Balance</th>}
              <th className="px-4 py-3 text-left overline">Status</th>
              {isAdmin && <th className="px-4 py-3 text-right overline"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr
                key={o.id}
                className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                data-testid={`order-row-${o.id}`}
              >
                <td className="px-4 py-3">
                  <Link to={`/orders/${o.id}`} className="font-mono text-xs font-semibold hover:text-[#FF6B00]">
                    {o.id}
                  </Link>
                  {o.source === "web" && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded" title={`Web order ${o.shop_order_no || ""}`} data-testid={`web-badge-${o.id}`}>
                      🌐 Web
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold">{o.customer_name}</div>
                  {o.customer_company && (
                    <div className="text-xs text-muted-foreground">{o.customer_company}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div>{o.product_name}</div>
                  <div className="text-xs text-muted-foreground">{o.product_category}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono">{o.quantity}</td>
                {showAmount && (
                  <td className="px-4 py-3 text-right font-mono">
                    ₹{Math.round(o.estimated_cost || 0).toLocaleString("en-IN")}
                  </td>
                )}
                {showAmount && (
                  <td className="px-4 py-3 text-right font-mono text-[#FF6B00]">
                    ₹{Math.round(o.balance || 0).toLocaleString("en-IN")}
                  </td>
                )}
                <td className="px-4 py-3">
                  <StatusBadge status={o.status} />
                </td>
                {isAdmin && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={(e) => { e.stopPropagation(); openPreview(o); }}
                      className="p-1.5 text-zinc-500 hover:text-[#FF6B00]"
                      title="Preview invoice PDF"
                      data-testid={`preview-invoice-${o.id}`}
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openWhatsAppShare(o); }}
                      disabled={waLoading === o.id}
                      className="p-1.5 text-zinc-500 hover:text-emerald-600 disabled:opacity-40"
                      title="Send invoice on WhatsApp"
                      data-testid={`whatsapp-invoice-${o.id}`}
                    >
                      <MessageCircle size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeOrder(o); }}
                      className="p-1.5 hover:text-[#FF6B00]"
                      title="Delete order permanently"
                      data-testid={`delete-order-${o.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={showAmount ? (isAdmin ? 8 : 7) : (isAdmin ? 6 : 5)} className="px-4 py-12 text-center text-muted-foreground">
                  No orders match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Bulk Invoice Export Modal */}
      {bulk.open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setBulk({ ...bulk, open: false })} data-testid="bulk-export-modal">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <div>
                <div className="overline text-[#FF6B00]">GSTR-1 Helper</div>
                <div className="font-display text-xl font-black tracking-tighter">Bulk Invoice Export</div>
              </div>
              <button onClick={() => setBulk({ ...bulk, open: false })} className="p-2 hover:bg-zinc-100 rounded-full" data-testid="bulk-close-btn"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">From</label>
                  <input type="date" value={bulk.from} onChange={(e) => setBulk({ ...bulk, from: e.target.value })}
                    className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none"
                    data-testid="bulk-from" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">To</label>
                  <input type="date" value={bulk.to} onChange={(e) => setBulk({ ...bulk, to: e.target.value })}
                    className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none"
                    data-testid="bulk-to" />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">Format</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: "both", l: "ZIP + CSV", d: "PDFs + GSTR-1 summary" },
                    { v: "zip", l: "ZIP only", d: "PDF invoices" },
                    { v: "csv", l: "CSV only", d: "Summary sheet" },
                  ].map((o) => {
                    const sel = bulk.fmt === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setBulk({ ...bulk, fmt: o.v })}
                        className={`text-left p-3 rounded-xl border-2 transition-all ${sel ? "border-[#FF6B00] bg-orange-50" : "border-zinc-200 hover:border-zinc-400"}`}
                        data-testid={`bulk-fmt-${o.v}`}
                      >
                        <div className="font-bold text-xs">{o.l}</div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">{o.d}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">Source filter</label>
                <div className="inline-flex bg-zinc-100 rounded-full p-0.5">
                  {[{ v: "", l: "All" }, { v: "web", l: "🌐 Web only" }, { v: "manual", l: "🏪 Manual only" }].map((o) => {
                    const sel = bulk.source === o.v;
                    return (
                      <button key={o.v || "all"} type="button" onClick={() => setBulk({ ...bulk, source: o.v })}
                        className={`px-3 py-1.5 text-xs font-bold rounded-full ${sel ? "bg-[#FF6B00] text-white" : "text-zinc-600"}`}
                        data-testid={`bulk-src-${o.v || "all"}`}>
                        {o.l}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                <input type="checkbox" checked={bulk.paid_only} onChange={(e) => setBulk({ ...bulk, paid_only: e.target.checked })} data-testid="bulk-paid-only" />
                Include only paid orders
              </label>

              <div className="text-[11px] text-zinc-500 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <b>💡 Tip:</b> The CSV summary follows the GSTR-1 layout — Taxable, CGST, SGST, IGST are auto-split based on the customer's state (Maharashtra = intra-state, others = inter-state IGST).
              </div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
              <button onClick={() => setBulk({ ...bulk, open: false })} className="btn-swiss btn-outline text-xs" data-testid="bulk-cancel">Cancel</button>
              <button onClick={runBulkExport} disabled={bulking || !bulk.from || !bulk.to}
                className="btn-swiss btn-accent text-xs disabled:opacity-50" data-testid="bulk-download-btn">
                <Download size={12} /> {bulking ? "Preparing…" : "Download"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Share Modal */}
      {waShare && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closeWhatsApp} data-testid="wa-share-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-emerald-50">
              <div>
                <div className="overline text-emerald-700">Send Invoice via WhatsApp</div>
                <div className="font-mono text-sm font-bold">{waShare.order_id}</div>
              </div>
              <button onClick={closeWhatsApp} className="p-2 hover:bg-white rounded-full" data-testid="wa-close-btn"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">Customer WhatsApp</label>
                <div className="font-mono text-sm bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2">+{waShare.phone}</div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">Message Preview</label>
                <textarea
                  value={waShare.message}
                  readOnly
                  rows={7}
                  className="w-full text-sm border-2 border-zinc-200 rounded-xl px-3 py-2 bg-zinc-50 font-mono outline-none"
                  data-testid="wa-message-preview"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { navigator.clipboard.writeText(waShare.public_invoice_url); toast.success("Invoice link copied"); }}
                  className="btn-swiss btn-outline text-xs flex-1"
                  data-testid="wa-copy-link"
                >
                  <Copy size={12} /> Copy invoice link
                </button>
                <a
                  href={waShare.public_invoice_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-swiss btn-outline text-xs flex-1"
                  data-testid="wa-preview-link"
                >
                  <Eye size={12} /> Preview PDF
                </a>
              </div>
              <div className="text-[11px] text-zinc-500 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <b>🔗 How it works:</b> The link is a permanent, tokenized public URL. Anyone with the link can view the invoice PDF — safe to share on WhatsApp, email, or SMS.
              </div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
              <a
                href={waShare.wa_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-5 py-2 rounded-full text-sm"
                data-testid="wa-open-btn"
              >
                <MessageCircle size={14} /> Open WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}

      {previewUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closePreview} data-testid="invoice-preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
              <div>
                <div className="overline text-[#FF6B00]">Invoice Preview</div>
                <div className="font-mono text-sm font-bold">{previewOrderId}</div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={previewUrl}
                  download={`Invoice-${previewOrderId}.pdf`}
                  className="btn-swiss btn-outline text-xs"
                  data-testid="preview-download-btn"
                >
                  <Download size={12} /> Download
                </a>
                <button
                  type="button"
                  onClick={closePreview}
                  className="p-2 hover:bg-zinc-100 rounded-full"
                  data-testid="preview-close-btn"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <iframe title="Invoice PDF Preview" src={previewUrl} className="flex-1 w-full" />
          </div>
        </div>
      )}
    </div>
  );
}
